import type {
  Instrument,
  MacdResponse,
  MarketEvent,
  OptionBarsResponse,
  OptionChainResponse,
  OptionInterval,
  ReadinessResponse,
  StockBarsResponse,
  StockInterval,
  StockSnapshot,
} from "./types";

const API_BASE = import.meta.env.VITE_API_BASE ?? "";

function historyQuery(
  interval: StockInterval,
  limit: number,
  before?: string,
): string {
  const query = new URLSearchParams({
    interval,
    limit: String(limit),
  });
  if (before) query.set("before", before);
  return query.toString();
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      ...init?.headers,
    },
  });
  if (!response.ok) {
    const detail = await response.text();
    throw new Error(detail || `${response.status} ${response.statusText}`);
  }
  return response.json() as Promise<T>;
}

export const marketApi = {
  readiness: async () => {
    const response = await fetch(`${API_BASE}/health/ready`, {
      headers: { Accept: "application/json" },
    });
    const payload: unknown = await response.json();
    if (!payload || typeof payload !== "object" || !("status" in payload)) {
      throw new Error("Invalid readiness response");
    }
    return payload as ReadinessResponse;
  },

  instruments: () =>
    request<{ instruments: Instrument[] }>("/api/instruments"),

  snapshot: (symbol: string) =>
    request<StockSnapshot>(`/api/stocks/${symbol}/snapshot`),

  bars: (
    symbol: string,
    interval: StockInterval,
    limit: number,
    before?: string,
  ) =>
    request<StockBarsResponse>(
      `/api/stocks/${symbol}/bars?${historyQuery(interval, limit, before)}`,
    ),

  macd: (
    symbol: string,
    interval: StockInterval,
    limit: number,
    before?: string,
  ) =>
    request<MacdResponse>(
      `/api/stocks/${symbol}/macd?${historyQuery(interval, limit, before)}`,
    ),

  expirations: (symbol: string) =>
    request<{ symbol: string; expirations: string[] }>(
      `/api/options/${symbol}/expirations`,
    ),

  chain: (symbol: string, expiration: string) =>
    request<OptionChainResponse>(
      `/api/options/${symbol}/chain?expiration=${expiration}`,
    ),

  optionBars: (
    symbol: string,
    contract: string,
    interval: OptionInterval,
  ) =>
    request<OptionBarsResponse>(
      `/api/options/${symbol}/bars?contract=${encodeURIComponent(contract)}&interval=${interval}&limit=600`,
    ),

  selectOption: (
    symbol: string,
    selection: { expiration?: string; contract?: string },
  ) =>
    request<{ updated: boolean }>(`/api/options/${symbol}/selection`, {
      method: "POST",
      body: JSON.stringify(selection),
    }),
};

export function subscribeToMarket(
  onEvent: (event: MarketEvent) => void,
  onStatus: (connected: boolean) => void,
): () => void {
  const stream = new EventSource(`${API_BASE}/api/stream`);
  const handleMarket = (event: MessageEvent<string>) => {
    try {
      onEvent(JSON.parse(event.data) as MarketEvent);
    } catch {
      // Ignore malformed provider events; the next valid update will reconcile.
    }
  };
  stream.addEventListener("connected", () => onStatus(true));
  stream.addEventListener("market", handleMarket as EventListener);
  stream.onerror = () => onStatus(false);
  return () => {
    stream.removeEventListener("market", handleMarket as EventListener);
    stream.close();
  };
}

export function intervalLimit(interval: StockInterval): number {
  switch (interval) {
    case "1m":
      return 390;
    case "5m":
      return 78;
    case "30m":
      return 13;
    case "daily":
      return 1260;
    case "weekly":
      return 260;
    case "monthly":
      return 60;
    case "quarterly":
      return 20;
  }
}

export function intradayHistoryPageLimit(interval: StockInterval): number {
  switch (interval) {
    case "1m":
      return 390;
    case "5m":
      return 78;
    case "30m":
      return 13;
    default:
      return 0;
  }
}
